import { values } from "lodash";
import memoizeOne from "memoize-one";
import { useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { filterMap } from "../lib/util/filterMap";
import { StoreState } from "../store/configureStore";
import { VesselRootObject } from "./ferry_types";
import { PortRootObject } from "./port_types";
import { RouteRootObject } from "./route_types";
import { Ferry } from "./useFerries";

export type FerryVessel = {
  ferry: Ferry;
  vessel: VesselRootObject;
};

export type MapFeature = PortRootObject | RouteRootObject | FerryVessel;

export const useMapFeature = (
  type: keyof StoreState | null,
  id: number
): MapFeature | undefined => {
  const mapState = useCallback(
    (state: StoreState) => getFeatureByTypeAndId(state, type, id),
    [type, id]
  );

  return useSelector(mapState);
};

export const useMapFeatures = (
  type: keyof StoreState | null,
  ids: number[]
): Array<MapFeature> => {
  const mapState = useCallback(
    (state: StoreState) =>
      filterMap(ids, (id) => getFeatureByTypeAndId(state, type, id)),
    [type, ids]
  );

  return useSelector(mapState);
};

export const useDistinctMapFeature = (
  type: keyof StoreState | null,
  id: number
): MapFeature | undefined => {
  const selectedFeature = useMapFeature(type, id);
  const ref = useRef<MapFeature | undefined>(undefined);

  if (!ref.current || !selectedFeature) {
    ref.current = selectedFeature;
  } else if (
    "type" in ref.current &&
    "type" in selectedFeature &&
    ref.current.type === selectedFeature.type
  ) {
    if (ref.current.id !== selectedFeature.id) {
      ref.current = selectedFeature;
    }
  } else if ("ferry" in ref.current && "ferry" in selectedFeature) {
    if (ref.current.ferry.imo !== selectedFeature.ferry.imo) {
      ref.current = selectedFeature;
    }
  } else {
    ref.current = selectedFeature;
  }

  return ref.current;
};

const createFerryVessel = memoizeOne<
  (vessel: VesselRootObject, ferry: Ferry) => FerryVessel
>((vessel, ferry) => ({
  vessel,
  ferry,
}));

const getFeatureByTypeAndId = (
  state: StoreState,
  type: keyof StoreState | null,
  id: number
): MapFeature | undefined => {
  if (type === "vessels") {
    const vessel = state.vessels[id];
    const ferry = state.ferries[Number(vessel?.acf.details.imo)];
    if (vessel && ferry) {
      return createFerryVessel(vessel, ferry);
    } else {
      return undefined;
    }
  } else if (type === "ferries") {
    const imo = String(id);
    const ferry = state.ferries[id];
    const vessels = values(state.vessels);

    const vessel = vessels.find((v) => Number(v?.acf.details.imo) === Number(imo));
    if (!vessel) {
      if (vessels.length > 0) {
        console.error(`No vessel in WP with IMO: ${imo}`);
      }
      return undefined;
    }
    if (!ferry) {
      if (values(state.ferries).length > 0) {
        console.error(`No ferry with id: ${id}`);
      }
      return undefined;
    }
    return createFerryVessel(vessel, ferry);
  } else {
    return type ? state[type][id] : undefined;
  }
};
